import React, { useState } from "react";
import Modal from "../../../elements/Modal";
import Button from "../../../form/Button";
import Radio from "../../../form/Radio";
import Form from "../../../form/Form";
import useForm from "../../../../hooks/useForm";
import Input from "../../../form/Input";
import Textarea from "../../../form/Textarea";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { enquiryAdd } from "../../../../services/enquiryService";
import Errors from "../../../elements/Errors";
import { useParams } from "react-router-dom";
import { classNames } from "../../../../helpers/classNames";
import { t } from "i18next";

const validation = {
  email: { required: true, message: "Please enter your email!" },
  name: { required: true, message: "Please enter your name!" },
  report: { required: true, message: "Please select a reason!" },
};

const EventReportModal = ({ event, buttonClass }) => {
  const [open, setOpen] = useState(false);

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    resetField,
    formValidate,
  } = useForm({ validationRules: validation });

  const report_options = [
    { value: "refund", label: t("Request a Refund") },
    {
      value: "feedback",
      label: t("Ask a question or give feedback about the event"),
    },
    {
      value: "not_as_described",
      label: t("Event did not happen or was not as described"),
    },
    { value: "spam", label: t("Spam or misleading content") },
    {
      value: "unauthorized_tickets",
      label: t(
        "Event not authorised to sell tickets or report invalid tickets"
      ),
    },
    {
      value: "outside_payment",
      label: t("Payment required outside Eventbrite"),
    },
    { value: "harmful", label: t("Harmful Content") },
    { value: "regulated", label: t("Regulated Content or Activities") },
    { value: "explicit", label: t("Sexually Explicit Content") },
    { value: "hateful", label: t("Hateful Content") },
    { value: "violence", label: t("Violence or Extremism") },
    { value: "copyright", label: t("Copyright or Trademark Infringement") },
  ];

  const { mutate: submitReport, isPending } = useMutation({
    mutationFn: async (val) => {
      const payload = {
        ...val,
        reason: val?.report?.value,
        referenceId: event?._id,
        organization: event?.organizationData?._id,
        type: "event-report",
      };
      delete payload?.report;
      return enquiryAdd(payload);
    },
    onSuccess: (res) => {
      if (res?.status === 201) {
        resetField();
        toast.success(res?.message);
        setOpen(false);
      } else {
        toast.error(res?.message || res?.error);
      }
    },
  });

  return (
    <>
      <button
        onClick={() => {
          setOpen(true);
        }}
        type="button"
        className={classNames(
          "flex items-center gap-2 px-3 py-2 hover:bg-primary/30 text-primary font-medium rounded-md mx-auto transition-all duration-500",
          buttonClass
        )}
      >
        <i className="fa-regular fa-flag-swallowtail"></i>
        <span>{t("Report this event")}</span>
      </button>
      <Modal
        isOpen={open}
        setIsOpen={() => {
          setOpen(false);
        }}
        size="xl"
      >
        <Form handleSubmit={handleSubmit} onSubmit={submitReport}>
          <p className="font-medium">
            {t(
              "Please help Dancebrite investigate this event by providing information about why you're reporting it."
            )}
          </p>
          <div className="pt-3 pb-1 text-lg font-semibold">
            {t("Reason For Report")}
          </div>
          <ul className="space-y-1">
            {report_options?.map((opt, i) => (
              <li key={i}>
                <Radio
                  checked={values?.report?.value === opt?.value}
                  value={opt?.value}
                  radioName={"report"}
                  radioLabel={<div className="font-medium">{opt?.label}</div>}
                  onChange={(e) =>
                    handleChange({
                      target: {
                        name: e.target.name,
                        value: { value: e.target.value, label: opt?.label },
                      },
                    })
                  }
                />
              </li>
            ))}
          </ul>
          <Errors {...formValidate(errors, "report")} />
          <div className="space-y-3">
            <Input
              inputPlaceholder={t("Enter name")}
              onChange={handleChange}
              inputName={"name"}
              label={t("Name")}
              value={values?.name}
              {...formValidate(errors, "name")}
            />
            <Input
              label={t("Email")}
              inputPlaceholder={t("Enter email")}
              inputType="email"
              onChange={handleChange}
              inputName={"email"}
              value={values?.email}
              {...formValidate(errors, "email")}
            />
            <Textarea
              inputPlaceholder={t("Enter report description")}
              onChange={handleChange}
              inputName={"message"}
              inputValue={values?.message}
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              buttonType="submit"
              buttonLabel={t("Submit report")}
              {...(isPending && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default EventReportModal;
